import type React from "react";
import { FormattedHTMLMessage } from "react-intl";

import { ExpandableImage } from "@/components/ExpandableImage/ExpandableImage";
import { AnnouncingHeader } from "@/components/AnnouncingHeader/AnnouncingHeader";

import { MAX_UPLOAD_RETRIES } from "./constants";

interface IdCheckReviewScreenProps {
  frontImage?: string;
  backImage?: string;
  hasImageError: boolean;
  currentUploadAttempt: number;
  onRetry: () => void;
  onSubmit: () => void;
}

export const IdCheckReviewScreen: React.FC<IdCheckReviewScreenProps> = ({
  frontImage,
  backImage,
  hasImageError,
  currentUploadAttempt,
  onRetry,
  onSubmit,
}) => (
  <div className="sid-header sid-l-horz-center" data-testid="sid-review-pending">
    <AnnouncingHeader align="center">
      <FormattedHTMLMessage
        id="step.idCheckLoop.regula.sdk.title"
        defaultMessage="Identity document upload"
        tagName="h1"
      />
    </AnnouncingHeader>

    <div className="sid-header__subtitle">
      <FormattedHTMLMessage
        id="step.idCheckLoop.regula.sdk.subtitle"
        defaultMessage="Submit a photo of your government-issued ID to continue verification."
      />
    </div>

    {hasImageError && currentUploadAttempt < MAX_UPLOAD_RETRIES && (
      <div className="sid-step-id-check-loop__error-wrapper">
        <div className="sid-step-id-check-loop__error-header">
          <FormattedHTMLMessage
            id="step.idCheckLoop.regula.sdk.imageQualityErrorTitle"
            defaultMessage="We couldn't process your document"
          />
        </div>
        <ul className="sid-step-id-check-loop__error-list">
          <li className="sid-step-id-check-loop__error-list-li">
            <FormattedHTMLMessage
              id="step.idCheckLoop.regula.sdk.imageQualityError"
              defaultMessage="The image has quality issues. Make sure it's clear, well-lit, and all details are visible."
            />
          </li>
        </ul>
        <div className="sid-step-id-check-loop__error-next-step">
          <FormattedHTMLMessage
            id="step.idCheckLoop.regula.sdk.rejectedImageNextStep"
            defaultMessage="Please retake and upload again."
          />
        </div>
      </div>
    )}

    <div className="sid-step-id-check-loop__image-preview-wrapper">
      {frontImage && (
        <ExpandableImage
          src={`data:image/jpeg;base64,${frontImage}`}
          alt="front"
          imageContainerClassName="sid-step-id-check-loop__image-preview"
        />
      )}
      {backImage && (
        <ExpandableImage
          src={`data:image/jpeg;base64,${backImage}`}
          alt="back"
          imageContainerClassName="sid-step-id-check-loop__image-preview"
        />
      )}
    </div>

    <button
      onClick={onSubmit}
      type="button"
      className="sid-btn sid-btn--dark"
      aria-label="submit"
      data-testid="submit-button"
      disabled={hasImageError && currentUploadAttempt < MAX_UPLOAD_RETRIES}
    >
      <FormattedHTMLMessage id="step.idCheckLoop.regula.submit" defaultMessage="Submit" />
    </button>

    {currentUploadAttempt < MAX_UPLOAD_RETRIES && (
      <div className="sid-l-space-top-sm">
        <button
          onClick={onRetry}
          type="button"
          className="sid-btn sid-btn-light"
          aria-label="retry"
          data-testid="retry-button"
        >
          <FormattedHTMLMessage
            id="step.idCheckLoop.regula.sdk.uploadAgain"
            defaultMessage="Upload Again"
          />
        </button>
      </div>
    )}
  </div>
);
